import type { AccessLevel } from '@/interfaces/Access.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import {
    momentDateToUnzonedDateTime,
    type UnzonedDateTime,
    unzonedDateTimeToApiDateFormat,
    unzonedDateTimeToMomentDate
} from '@/interfaces/Date.interface';
import type { ProjectId } from '@/interfaces/Project.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import type { UnitCode } from '@/interfaces/Unit.interface';
import type { APIError } from '@/providers/errors.constants';
import {
    convertPaginatedRequestParamsToString,
    type CoreApiPaginatedRequestParams,
    type CoreApiPaginatedResponse,
    CoreApiVersionPath,
    RosaCoreApiService
} from '@/rosa';
import { restartOn } from '@/utils/rxjs/operators/restart-on.operator';
import { inject, Injectable } from '@angular/core';
import { filter, firstValueFrom, map, type Observable, Subject } from 'rxjs';
import {
    AlertOccupancyMode,
    type AlertScenarioId,
    type AlertScenarioStatus,
    type AlertScenarioType,
    type AlertStatPeriodCode,
    type AlertTypeCode,
    type AlertValidation
} from '../../alerts.interface';
import type {
    WaterLeakageAlertScenariosFilters,
    WaterLeakageCustomAlertEditorModel,
    WaterLeakageScenario,
    WaterLeakageScenarioAlertsTimeseriesAnalysis,
    WaterLeakageScenarioCreation,
    WaterLeakageScenarioGlobalSummary,
    WaterLeakageScenarioSensorStat,
    WaterLeakageScenarioStat,
    WaterLeakageScenarioSummary,
    WaterLeakageScenarioUpdate
} from '../water-leakage-alert-scenario.interface';
const sortedBackendDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

interface WaterLeakageScenarioSummaryResponse {
    alertScenarioId: AlertScenarioId;
    scenarioType: AlertScenarioType;
    name: string;
    access: AccessLevel;
    isActiveInOccupation: boolean;
    isActiveInInoccupation: boolean;
    alertCount: number;
    impactedBuildingCount: number;
    buildingCount: number;
    impactedSensorCount: number;
    sensorCount: number;
    status: AlertScenarioStatus;
    alertProgress: number | null;
    impactedBuildingProgress: number | null;
    impactedSensorProgress: number | null;
    deviationVal: number | null;
    deviationValProgress: number | null;
}

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreWaterLeakageScenarioService {
    private _rosaCoreApiService = inject(RosaCoreApiService);

    private _alertScenarioSaved$ = new Subject<AlertScenarioId>();

    getWaterLeakageScenario$(scenarioId: AlertScenarioId): Observable<WaterLeakageScenario> {
        interface RouteResponse {
            alertScenarioId: AlertScenarioId;
            scenarioType: AlertScenarioType.WATER_LEAKAGE;
            scenarioStatus: AlertScenarioStatus;
            name: string;
            access: AccessLevel;
            periodInfos: {
                isActiveInOccupation: boolean;
                isActiveInInoccupation: boolean;
                isActiveDuringSchoolHolidays: boolean;
                isActiveDuringPublicHolidays: boolean;
                periodStart: {
                    day: number;
                    month: number;
                };
                periodEnd: {
                    day: number;
                    month: number;
                };
                daysRanges: Record<
                    string,
                    {
                        isActivated: boolean;
                        ranges: {
                            start: string;
                            end: string;
                        }[];
                    }
                >;
            };
        }

        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenario/${scenarioId}`).pipe(
            restartOn(this._alertScenarioSaved$.pipe(filter(id => id === scenarioId))),
            map(s => ({
                scenario: {
                    scenarioId: s.alertScenarioId,
                    type: s.scenarioType,
                    status: s.scenarioStatus,
                    name: s.name,
                    access: s.access
                },
                waterLeakageConfig: {
                    activity: {
                        occupancy:
                            s.periodInfos.isActiveInInoccupation && s.periodInfos.isActiveInOccupation
                                ? AlertOccupancyMode.ALL
                                : s.periodInfos.isActiveInInoccupation
                                  ? AlertOccupancyMode.INOCCUPANCY
                                  : s.periodInfos.isActiveInOccupation
                                    ? AlertOccupancyMode.OCCUPANCY
                                    : AlertOccupancyMode.ALL,
                        alertHolidays: s.periodInfos.isActiveDuringSchoolHolidays,
                        alertPublicHolidays: s.periodInfos.isActiveDuringPublicHolidays,
                        periodStart: s.periodInfos.periodStart,
                        periodEnd: s.periodInfos.periodEnd,
                        weeklyRanges: Object.entries(s.periodInfos.daysRanges || {})
                            .sort(([day1], [day2]) => {
                                return sortedBackendDays.indexOf(day1) - sortedBackendDays.indexOf(day2);
                            })
                            .map(([_, v]) => ({
                                isActivated: v.isActivated,
                                ranges: v.ranges || []
                            }))
                    }
                }
            }))
        );
    }

    createWaterLeakageScenario(thresholdScenario: WaterLeakageScenarioCreation): Promise<AlertScenarioId> {
        interface CreateWaterLeakagedAlertScenarioDto {
            name: string;
        }

        const createWaterLeakageScenarioAlert: CreateWaterLeakagedAlertScenarioDto = {
            name: thresholdScenario.name
        };

        return firstValueFrom(
            this._rosaCoreApiService.post<AlertScenarioId>(
                `${CoreApiVersionPath.V5}alerts/scenario/water-leakage`,
                createWaterLeakageScenarioAlert
            )
        );
    }

    updateWaterLeakageScenario(alertScenarioId: AlertScenarioId, update: WaterLeakageScenarioUpdate): Promise<void> {
        interface RouteRequest {
            name?: string;
            isActiveInOccupation?: boolean;
            isActiveInInoccupation?: boolean;
            isActiveDuringSchoolHolidays?: boolean;
            isActiveDuringPublicHolidays?: boolean;
            periodStart?: {
                day: number;
                month: number;
            };
            periodEnd?: {
                day: number;
                month: number;
            };
            daysRanges?: Record<
                string,
                {
                    isActivated: boolean;
                    ranges: {
                        start: string;
                        end: string;
                    }[];
                }
            >;
        }

        const body: RouteRequest = {
            name: update.name,
            isActiveInOccupation: update.waterLeakageConfig?.activity?.occupancy
                ? update.waterLeakageConfig?.activity.occupancy === AlertOccupancyMode.ALL ||
                  update.waterLeakageConfig?.activity.occupancy === AlertOccupancyMode.OCCUPANCY
                : undefined,
            isActiveInInoccupation: update.waterLeakageConfig?.activity?.occupancy
                ? update.waterLeakageConfig?.activity?.occupancy === AlertOccupancyMode.ALL ||
                  update.waterLeakageConfig?.activity?.occupancy === AlertOccupancyMode.INOCCUPANCY
                : undefined,
            isActiveDuringSchoolHolidays: update.waterLeakageConfig?.activity?.alertHolidays,
            isActiveDuringPublicHolidays: update.waterLeakageConfig?.activity?.alertPublicHolidays,
            periodStart: update.waterLeakageConfig?.activity?.periodStart,
            periodEnd: update.waterLeakageConfig?.activity?.periodEnd,
            daysRanges: update.waterLeakageConfig?.activity?.weeklyRanges
                ? update.waterLeakageConfig?.activity?.weeklyRanges.reduce(
                      (acc, currentValue, index) => {
                          return {
                              ...acc,
                              [sortedBackendDays[index]]: { isActivated: currentValue.isActivated, ranges: currentValue.ranges }
                          };
                      },
                      {} as Record<
                          string,
                          {
                              isActivated: boolean;
                              ranges: {
                                  start: string;
                                  end: string;
                              }[];
                          }
                      >
                  )
                : undefined
        };

        return firstValueFrom(
            this._rosaCoreApiService.put<void>(`${CoreApiVersionPath.V5}alerts/scenario/${alertScenarioId}/water-leakage`, body)
        );
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/save`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/publish`, {})
        ).then(() => {
            this._alertScenarioSaved$.next(alertScenarioId);
        });
    }

    getAlertScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        interface RouteResponse {
            errors: APIError[];
            valid: boolean;
        }

        return this._rosaCoreApiService
            .get<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/check`)
            .pipe(restartOn(this._alertScenarioSaved$.pipe(filter(savedScenarioId => savedScenarioId === alertScenarioId))));
    }

    fetchAlertScenarios$(
        paginatedParams: CoreApiPaginatedRequestParams,
        periodCode: AlertStatPeriodCode,
        viewAllProjectScenarios: boolean,
        filters: WaterLeakageAlertScenariosFilters
    ): Observable<CoreApiPaginatedResponse<WaterLeakageScenarioSummary>> {
        interface WaterLeakageAlertScenarioFiltersDto {
            search: string | null;
            scenarioStatus: AlertScenarioStatus | null;
        }

        const ThresholdScenarioFilters: WaterLeakageAlertScenarioFiltersDto = {
            search: filters.search,
            scenarioStatus: filters.status
        };

        const params = convertPaginatedRequestParamsToString(paginatedParams);
        const url = viewAllProjectScenarios ? 'alerts/scenario/water-leakage/search/all' : 'alerts/scenario/water-leakage/search';
        return this._rosaCoreApiService
            .post<
                CoreApiPaginatedResponse<WaterLeakageScenarioSummaryResponse>
            >(`${CoreApiVersionPath.V5}${url}?periodCode=${periodCode}&${params}`, ThresholdScenarioFilters)
            .pipe(
                map(paginatedResponse => {
                    const alertScenarios: WaterLeakageScenarioSummary[] = paginatedResponse.content.map(alert =>
                        this.mapToAlertScenarioSummary(alert)
                    );

                    return { ...paginatedResponse, content: alertScenarios };
                })
            );
    }

    private mapToAlertScenarioSummary(scenario: WaterLeakageScenarioSummaryResponse): WaterLeakageScenarioSummary {
        let occupancy = AlertOccupancyMode.ALL;
        if (scenario.isActiveInInoccupation && !scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.INOCCUPANCY;
        } else if (!scenario.isActiveInInoccupation && scenario.isActiveInOccupation) {
            occupancy = AlertOccupancyMode.OCCUPANCY;
        }
        return {
            alertScenarioId: scenario.alertScenarioId,
            scenarioType: scenario.scenarioType,
            name: scenario.name,
            occupancy,
            alertCount: scenario.alertCount,
            impactedBuildingCount: scenario.impactedBuildingCount,
            buildingCount: scenario.buildingCount,
            impactedSensorCount: scenario.impactedSensorCount,
            sensorCount: scenario.sensorCount,
            status: scenario.status,
            access: scenario.access,
            alertProgress: scenario.alertProgress,
            impactedBuildingProgress: scenario.impactedBuildingProgress,
            impactedSensorProgress: scenario.impactedSensorProgress,
            deviationVal: scenario.deviationVal,
            deviationValProgress: scenario.deviationValProgress
        };
    }

    getWaterLeakageScenarioAlertsTimeseriesAnalysis$(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<WaterLeakageScenarioAlertsTimeseriesAnalysis> {
        interface RouteRequest {
            startDate: string;
            endDate: string;
        }

        interface RouteResponse {
            sensorTimeseries: {
                datetime: UnzonedDateTime;
                value: number;
            }[];
            sensorBoundsTimeseries: {
                datetime: UnzonedDateTime;
                lowBound: number;
                highBound: number;
            }[];
            sensorTimeseriesUnitCode: UnitCode;
            waterLeakageActivityTimeseries: {
                datetime: UnzonedDateTime;
                activeAfter: boolean;
                inactivityStatuses: string[];
            }[];
            waterLeakageAlerts: {
                projectId: ProjectId;
                buildingId: BuildingId;
                timeseriesId: SensorTimeseriesId;
                scenarioId: AlertScenarioId;
                alertId: number;
                alertTypeCode: AlertTypeCode;
                status: string;
                startedAt: UnzonedDateTime;
                endedAt: UnzonedDateTime;
                doneBy: string;
                doneAt: UnzonedDateTime;
                activeAfter: boolean;
                reason: string | null;
            }[];
        }
        const body: RouteRequest = {
            startDate: unzonedDateTimeToApiDateFormat(startDate),
            endDate: unzonedDateTimeToApiDateFormat(momentDateToUnzonedDateTime(unzonedDateTimeToMomentDate(endDate).add(1, 'day')))
        };

        return this._rosaCoreApiService
            .post<RouteResponse>(`${CoreApiVersionPath.V5}alerts/scenarios/${alertScenarioId}/analysis/${timeseriesId}/water-leakage`, body)
            .pipe(
                map(response => ({
                    sensorTimeseries: response.sensorTimeseries,
                    sensorBoundsTimeseries: response.sensorBoundsTimeseries,
                    sensorTimeseriesUnitCode: response.sensorTimeseriesUnitCode,
                    waterLeakageActivityTimeseries: response.waterLeakageActivityTimeseries,
                    waterLeakageAlerts: response.waterLeakageAlerts.map(alert => ({
                        projectId: alert.projectId,
                        buildingId: alert.buildingId,
                        timeseriesId: alert.timeseriesId,
                        scenarioId: alert.scenarioId,
                        alertId: alert.alertId,
                        alertTypeCode: alert.alertTypeCode,
                        status: alert.status,
                        startedAt: alert.startedAt,
                        endedAt: alert.endedAt,
                        doneBy: alert.doneBy,
                        doneAt: alert.doneAt,
                        activeAfter: alert.activeAfter,
                        reason: alert.reason
                    }))
                }))
            );
    }

    fetchAlertScenarioBuildingsStats$(
        paginatedParams: CoreApiPaginatedRequestParams,
        alertScenarioId: AlertScenarioId,
        periodCode: AlertStatPeriodCode,
        search: string | null
    ): Observable<CoreApiPaginatedResponse<WaterLeakageScenarioStat>> {
        interface RouteResponse {
            buildingId: BuildingId;
            sensorCount: number;
            alertCount: number;
            deviationVal: number | null;
        }
        const params = convertPaginatedRequestParamsToString(paginatedParams);
        return this._rosaCoreApiService.post<CoreApiPaginatedResponse<RouteResponse>>(
            `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/buildings-stats/search?periodCode=${periodCode}&${params}`,
            { search }
        );
    }

    fetchAlertScenarioSensorStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<WaterLeakageScenarioSensorStat[]> {
        interface RouteResponse {
            timeseriesId: SensorTimeseriesId;
            alertCount: number;
        }
        return this._rosaCoreApiService.post<RouteResponse[]>(
            `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/buildings/${buildingId}/sensors-stats`,
            {
                startDate: unzonedDateTimeToApiDateFormat(startDate),
                endDate: unzonedDateTimeToApiDateFormat(momentDateToUnzonedDateTime(unzonedDateTimeToMomentDate(endDate).add(1, 'day')))
            }
        );
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<WaterLeakageScenarioSummary> {
        return this._rosaCoreApiService
            .get<WaterLeakageScenarioSummaryResponse>(
                `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/summary?periodCode=${periodCode}`
            )
            .pipe(map(alert => this.mapToAlertScenarioSummary(alert)));
    }

    getAlertScenarioGlobalSummary$(periodCode: AlertStatPeriodCode): Observable<WaterLeakageScenarioGlobalSummary> {
        interface RouteResponse {
            alertCount: number;
            impactedBuildingCount: number;
            buildingCount: number;
            impactedSensorCount: number;
            sensorCount: number;
            alertProgress: number | null;
            impactedBuildingProgress: number | null;
            impactedSensorProgress: number | null;
            deviationVal: number | null;
            deviationValProgress: number | null;
        }
        return this._rosaCoreApiService.get<RouteResponse>(
            `${CoreApiVersionPath.V5}alerts/scenario/water-leakage/global-stats?periodCode=${periodCode}`
        );
    }

    declareAlert(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        customAlert: WaterLeakageCustomAlertEditorModel
    ): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.post<void>(
                `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/sen/${timeseriesId}/declare-alert`,
                customAlert
            )
        );
    }

    deleteAnnotatedAlert(alertScenarioId: AlertScenarioId, alertId: number): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.delete<void>(
                `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/user-detected-alerts/${alertId}`
            )
        );
    }

    updateAnnotatedAlert(
        alertScenarioId: AlertScenarioId,
        alertId: number,
        customAlert: WaterLeakageCustomAlertEditorModel
    ): Promise<void> {
        return firstValueFrom(
            this._rosaCoreApiService.put<void>(
                `${CoreApiVersionPath.V5}alerts/scenarios/water-leakage/${alertScenarioId}/user-detected-alerts/${alertId}`,
                customAlert
            )
        );
    }
}
